import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, MapPin, Ruler, Trash2, RotateCcw, Search, Home } from 'lucide-react';
import GoogleMapsLoader from '../GoogleMapsLoader';

export interface MeasurementResult {
  title: string;
  area_sqft: number;
  area_sqm: number;
  perimeter_ft: number;
  polygon_coords: { lat: number; lng: number }[];
  address: string;
  map_center_lat: number;
  map_center_lng: number;
  map_zoom: number;
}

interface GoogleMeasurementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (measurement: MeasurementResult) => void;
  initialMeasurement?: MeasurementResult | null;
  customerAddress?: string | null;
}

function MeasurementMapInner({
  onSave,
  onClose,
  initialMeasurement,
  customerAddress,
}: Omit<GoogleMeasurementModalProps, 'isOpen'>) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const polygonRef = useRef<google.maps.Polygon | null>(null);
  const drawingManagerRef = useRef<google.maps.drawing.DrawingManager | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const [title, setTitle] = useState(initialMeasurement?.title || 'New Measurement');
  const [address, setAddress] = useState(initialMeasurement?.address || '');
  const [areaSqft, setAreaSqft] = useState(initialMeasurement?.area_sqft || 0);
  const [areaSqm, setAreaSqm] = useState(initialMeasurement?.area_sqm || 0);
  const [perimeterFt, setPerimeterFt] = useState(initialMeasurement?.perimeter_ft || 0);
  const [hasPolygon, setHasPolygon] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [mapReady, setMapReady] = useState(false);

  const SQM_TO_SQFT = 10.7639;
  const M_TO_FT = 3.28084;

  const recalculate = useCallback((polygon: google.maps.Polygon) => {
    const path = polygon.getPath();
    if (path.getLength() < 3) {
      setAreaSqft(0);
      setAreaSqm(0);
      setPerimeterFt(0);
      return;
    }

    const areaM2 = google.maps.geometry.spherical.computeArea(path);
    const sqm = Math.round(areaM2 * 100) / 100;
    const sqft = Math.round(areaM2 * SQM_TO_SQFT * 100) / 100;

    const perimeterM = google.maps.geometry.spherical.computeLength(path);
    const permFt = Math.round(perimeterM * M_TO_FT * 100) / 100;

    setAreaSqm(sqm);
    setAreaSqft(sqft);
    setPerimeterFt(permFt);
  }, []);

  const attachPathListeners = useCallback((polygon: google.maps.Polygon) => {
    const path = polygon.getPath();
    google.maps.event.addListener(path, 'set_at', () => recalculate(polygon));
    google.maps.event.addListener(path, 'insert_at', () => recalculate(polygon));
    google.maps.event.addListener(path, 'remove_at', () => recalculate(polygon));
  }, [recalculate]);

  const getUserLocation = useCallback(async (): Promise<{ lat: number; lng: number } | null> => {
    try {
      const { backgroundLocationService } = await import('../../services/backgroundLocationService');
      const pos = await backgroundLocationService.getPosition(false);
      if (pos?.coords) return { lat: pos.coords.latitude, lng: pos.coords.longitude };
    } catch { /* native plugin unavailable */ }
    return null;
  }, []);

  const goToCustomerAddress = useCallback(async () => {
    if (!customerAddress || !mapInstanceRef.current) return;
    try {
      const geocoder = new google.maps.Geocoder();
      const result = await geocoder.geocode({ address: customerAddress });
      if (result.results[0]?.geometry?.location) {
        mapInstanceRef.current.setCenter(result.results[0].geometry.location);
        mapInstanceRef.current.setZoom(20);
        setAddress(result.results[0].formatted_address || customerAddress);
      }
    } catch (e) {
      console.error('Geocode failed:', e);
    }
  }, [customerAddress]);

  useEffect(() => {
    if (!mapRef.current || mapReady) return;
    let cancelled = false;

    const loadAndInit = async () => {
      // Dynamically import drawing & geometry libraries if not already loaded
      try {
        if (!window.google?.maps?.drawing) {
          await google.maps.importLibrary('drawing');
        }
        if (!window.google?.maps?.geometry) {
          await google.maps.importLibrary('geometry');
        }
      } catch (err) {
        console.error('Failed to load Google Maps libraries:', err);
        return;
      }

      if (cancelled || !mapRef.current) return;
      initMap();
    };

    const initMap = () => {
      if (!mapRef.current) return;

      const defaultCenter = initialMeasurement
        ? { lat: initialMeasurement.map_center_lat, lng: initialMeasurement.map_center_lng }
        : { lat: 49.2827, lng: -123.1207 };

      const map = new google.maps.Map(mapRef.current, {
        center: defaultCenter,
        zoom: initialMeasurement?.map_zoom || 19,
        mapTypeId: 'satellite',
        tilt: 0,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
        gestureHandling: 'greedy',
      });

      mapInstanceRef.current = map;

      const drawingManager = new google.maps.drawing.DrawingManager({
      drawingMode: null,
      drawingControl: false,
      polygonOptions: {
        fillColor: '#3b82f6',
        fillOpacity: 0.25,
        strokeColor: '#3b82f6',
        strokeOpacity: 0.9,
        strokeWeight: 2,
        editable: true,
        draggable: true,
      },
    });
    drawingManager.setMap(map);
    drawingManagerRef.current = drawingManager;

    google.maps.event.addListener(drawingManager, 'polygoncomplete', (polygon: google.maps.Polygon) => {
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
      }
      polygonRef.current = polygon;
      drawingManager.setDrawingMode(null);
      setIsDrawing(false);
      setHasPolygon(true);
      recalculate(polygon);
      attachPathListeners(polygon);
    });

    if (initialMeasurement?.polygon_coords && initialMeasurement.polygon_coords.length >= 3) {
      const polygon = new google.maps.Polygon({
        paths: initialMeasurement.polygon_coords,
        fillColor: '#3b82f6',
        fillOpacity: 0.25,
        strokeColor: '#3b82f6',
        strokeOpacity: 0.9,
        strokeWeight: 2,
        editable: true,
        draggable: true,
        map,
      });
      polygonRef.current = polygon;
      setHasPolygon(true);
      recalculate(polygon);
      attachPathListeners(polygon);
    }

    if (searchInputRef.current && window.google?.maps?.places) {
      const autocomplete = new google.maps.places.Autocomplete(searchInputRef.current, {
        types: ['address'],
      });
      autocomplete.bindTo('bounds', map);
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.geometry?.location) {
          map.setCenter(place.geometry.location);
          map.setZoom(20);
          setAddress(place.formatted_address || '');
        }
      });
      autocompleteRef.current = autocomplete;
    }

      // Center on user's current location for new measurements
      if (!initialMeasurement) {
        getUserLocation().then(loc => {
          if (loc && !cancelled && mapInstanceRef.current) {
            mapInstanceRef.current.setCenter(loc);
            mapInstanceRef.current.setZoom(19);
          }
        });
      }

      setMapReady(true);
    };

    loadAndInit();

    return () => {
      cancelled = true;
      if (polygonRef.current) polygonRef.current.setMap(null);
      if (drawingManagerRef.current) drawingManagerRef.current.setMap(null);
    };
  }, []);

  const startDrawing = () => {
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
      setHasPolygon(false);
      setAreaSqft(0);
      setAreaSqm(0);
      setPerimeterFt(0);
    }
    drawingManagerRef.current?.setDrawingMode(google.maps.drawing.OverlayType.POLYGON);
    setIsDrawing(true);
  };

  const clearPolygon = () => {
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }
    setHasPolygon(false);
    setAreaSqft(0);
    setAreaSqm(0);
    setPerimeterFt(0);
    drawingManagerRef.current?.setDrawingMode(null);
    setIsDrawing(false);
  };

  const handleSave = () => {
    if (!title.trim()) return;
    if (!polygonRef.current) return;

    const path = polygonRef.current.getPath();
    const coords: { lat: number; lng: number }[] = [];
    for (let i = 0; i < path.getLength(); i++) {
      const p = path.getAt(i);
      coords.push({ lat: p.lat(), lng: p.lng() });
    }

    const center = mapInstanceRef.current?.getCenter();
    const zoom = mapInstanceRef.current?.getZoom();

    onSave({
      title: title.trim(),
      area_sqft: areaSqft,
      area_sqm: areaSqm,
      perimeter_ft: perimeterFt,
      polygon_coords: coords,
      address,
      map_center_lat: center?.lat() || 0,
      map_center_lng: center?.lng() || 0,
      map_zoom: zoom || 19,
    });
  };

  const canSave = title.trim() && hasPolygon && areaSqft > 0;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-20"
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        <X className="h-5 w-5" />
      </button>

      {/* Header */}
      <div className="bg-gray-50 border-b border-gray-200 px-6 py-5 text-center relative flex-shrink-0">
        <h2 className="text-xl font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
          Google Earth Measurement
        </h2>
        <p className="text-sm text-gray-500 mt-1">Draw a polygon to measure area</p>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Title + Address bar */}
        <div className="px-4 py-3 border-b border-gray-200 space-y-2 flex-shrink-0">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Measurement title (e.g. Front Driveway, Backyard)"
            className="w-full px-3 py-2.5 text-sm font-medium bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            style={{ fontSize: '16px' }}
          />
          <div className="relative flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                ref={searchInputRef}
                type="text"
                defaultValue={address}
                placeholder="Search address to navigate..."
                className="w-full pl-9 pr-3 py-2.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                style={{ fontSize: '16px' }}
              />
            </div>
            {customerAddress && (
              <button
                type="button"
                onClick={goToCustomerAddress}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition-colors touch-manipulation flex-shrink-0"
                style={{ WebkitTapHighlightColor: 'transparent' }}
                title="Use customer address"
              >
                <Home className="h-3.5 w-3.5" />
                Customer
              </button>
            )}
          </div>
        </div>

        {/* Map area */}
        <div className="flex-1 relative min-h-0">
          <div ref={mapRef} className="absolute inset-0" />

          {/* Drawing toolbar — overlays on map */}
          <div className="absolute top-3 left-3 flex gap-2 z-10">
            <button
              type="button"
              onClick={startDrawing}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg shadow-lg transition-colors touch-manipulation ${
                isDrawing
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
              }`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <MapPin className="h-3.5 w-3.5" />
              {hasPolygon ? 'Redraw' : 'Draw Area'}
            </button>
            {hasPolygon && (
              <button
                type="button"
                onClick={clearPolygon}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg shadow-lg bg-white text-red-600 hover:bg-red-50 border border-gray-200 transition-colors touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Clear
              </button>
            )}
          </div>

          {/* Measurement readout — overlays on map */}
          {hasPolygon && areaSqft > 0 && (
            <div className="absolute bottom-3 left-3 right-3 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200 px-4 py-3 z-10">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {areaSqft.toLocaleString()} <span className="text-sm font-medium text-gray-500">sq ft</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {areaSqm.toLocaleString()} sq m · Perimeter: {perimeterFt.toLocaleString()} ft
                  </div>
                </div>
                <Ruler className="h-5 w-5 text-green-600 flex-shrink-0" />
              </div>
            </div>
          )}

          {/* Drawing hint */}
          {isDrawing && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-semibold px-4 py-2 rounded-full shadow-lg z-10 pointer-events-none">
              Click to place points · Click first point to close
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 bg-white px-5 py-4 flex-shrink-0">
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          className={`w-full inline-flex items-center justify-center px-6 py-3 text-base font-semibold text-white rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm disabled:cursor-not-allowed disabled:transform-none ${
            canSave
              ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
              : 'bg-gray-300'
          }`}
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          {canSave ? `Save Measurement — ${areaSqft.toLocaleString()} sq ft` : 'Draw an area to measure'}
        </button>
      </div>
    </div>
  );
}

export default function GoogleMeasurementModal({
  isOpen,
  onClose,
  onSave,
  initialMeasurement,
  customerAddress,
}: GoogleMeasurementModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) setIsAnimating(true);
  }, [isOpen]);

  const handleTransitionEnd = () => {
    if (!isOpen) setIsAnimating(false);
  };

  if (!isOpen && !isAnimating) return null;

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "AIzaSyCGC4-0ZxPH8WLgxMYBhVmyscFSxk0Yhx8";

  return createPortal(
    <div
      className={`fixed inset-0 transition-all duration-300 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      onClick={onClose}
      onTransitionEnd={handleTransitionEnd}
      style={{
        zIndex: 100200,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      <div
        className={`fixed inset-y-0 right-0 w-full md:w-[700px] bg-white shadow-2xl transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100200,
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <GoogleMapsLoader apiKey={apiKey}>
          <MeasurementMapInner
            onSave={onSave}
            onClose={onClose}
            initialMeasurement={initialMeasurement}
            customerAddress={customerAddress}
          />
        </GoogleMapsLoader>
      </div>
    </div>,
    document.body
  );
}
